/**
 * Host Analytics Dashboard Page
 * Analytics overview for event hosts.
 */

import React, { useState, useMemo } from 'react';
import { 
  BarChart3, 
  Users, 
  Calendar, 
  TrendingUp, 
  Download, 
  RefreshCw,
  Filter,
  X,
  Clock,
  UserCheck,
  Activity,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import AnalyticsCard from '../../components/Analytics/AnalyticsCard';
import Button from '../../components/UI/Button';
import DatePicker from '../../components/UI/DatePicker';
import Select from '../../components/UI/Select';
import { useHostAnalytics, useAnalyticsExport } from '../../hooks/useAnalytics';
import { useAppSelector } from '../../store/hooks';

const HostAnalyticsPage: React.FC = () => {
  const { theme } = useAppSelector((state) => state.ui);
  const { exporting, exportEvents } = useAnalyticsExport();
  
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    sort_order: 'oldest' as 'oldest' | 'newest'
  });
  const [dateError, setDateError] = useState<string>('');
  const [expandedSeries, setExpandedSeries] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Validate dates
  React.useEffect(() => {
    if (filters.start_date && filters.end_date) {
      const startDate = new Date(filters.start_date);
      const endDate = new Date(filters.end_date);
      if (startDate > endDate) {
        setDateError('End date must be after start date');
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  }, [filters.start_date, filters.end_date]);
  
  // Convert filters to format expected by API (ISO date strings)
  const analyticsFilters = useMemo(() => {
    // Don't send filters if there's a date validation error
    if (dateError) {
      return undefined;
    }
    
    const apiFilters: { start_date?: string; end_date?: string } = {};
    if (filters.start_date) {
      // HTML date inputs return YYYY-MM-DD format
      // Create a date at midnight local time, then convert to ISO
      const [year, month, day] = filters.start_date.split('-').map(Number);
      const startDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (!isNaN(startDate.getTime())) {
        // Convert to ISO string (will include timezone offset)
        apiFilters.start_date = startDate.toISOString();
      }
    }
    if (filters.end_date) {
      // HTML date inputs return YYYY-MM-DD format
      // Create a date at end of day local time, then convert to ISO
      const [year, month, day] = filters.end_date.split('-').map(Number);
      const endDate = new Date(year, month - 1, day, 23, 59, 59, 999);
      if (!isNaN(endDate.getTime())) {
        // Convert to ISO string (will include timezone offset)
        apiFilters.end_date = endDate.toISOString();
      }
    }
    return Object.keys(apiFilters).length > 0 ? apiFilters : undefined;
  }, [filters.start_date, filters.end_date, dateError]);

  const { data, loading, error, refetch } = useHostAnalytics(analyticsFilters);

  // Group events by series and prepare for display (before early returns)
  const groupedEvents = useMemo(() => {
    if (!data?.recent_events) return [];
    
    // Sort events by date (oldest to newest by default)
    const sortedEvents = [...data.recent_events].sort((a, b) => {
      const dateA = new Date(a.event_date).getTime();
      const dateB = new Date(b.event_date).getTime();
      return filters.sort_order === 'oldest' ? dateA - dateB : dateB - dateA;
    });
    
    const seriesMap = new Map<string, typeof data.recent_events>();
    const standaloneEvents: typeof data.recent_events = [];

    // Group events by series
    sortedEvents.forEach(event => {
      if (event.series_id && event.series_name) {
        if (!seriesMap.has(event.series_id)) {
          seriesMap.set(event.series_id, []);
        }
        seriesMap.get(event.series_id)!.push(event);
      } else {
        standaloneEvents.push(event);
      }
    });

    // Convert series map to array of items
    const items: Array<{ type: 'series' | 'event'; seriesId?: string; seriesName?: string; events: typeof data.recent_events }> = [];
    
    // Add series items
    seriesMap.forEach((events, seriesId) => {
      const seriesName = events[0]?.series_name || 'Unknown Series';
      // Sort events within series by date
      const sortedSeriesEvents = [...events].sort((a, b) => {
        const dateA = new Date(a.event_date).getTime();
        const dateB = new Date(b.event_date).getTime();
        return filters.sort_order === 'oldest' ? dateA - dateB : dateB - dateA;
      });
      items.push({
        type: 'series',
        seriesId,
        seriesName,
        events: sortedSeriesEvents
      });
    });

    // Add standalone events
    standaloneEvents.forEach(event => {
      items.push({
        type: 'event',
        events: [event]
      });
    });

    return items;
  }, [data, filters.sort_order]);

  // Pagination logic
  const totalItems = groupedEvents.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = groupedEvents.slice(startIndex, endIndex);

  // Reset to page 1 when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data?.recent_events?.length]);

  const handleExport = async (format: 'csv' | 'excel') => {
    // Format dates properly for backend (ISO 8601 format)
    const exportRequest: any = {
      format,
      include_details: true
    };
    
    // Only include dates if they are provided and valid
    if (filters.start_date) {
      // Convert date string to ISO format with time
      const startDate = new Date(filters.start_date);
      if (!isNaN(startDate.getTime())) {
        exportRequest.start_date = startDate.toISOString();
      }
    }
    
    if (filters.end_date) {
      // Convert date string to ISO format with time (end of day)
      const endDate = new Date(filters.end_date);
      if (!isNaN(endDate.getTime())) {
        // Set to end of day
        endDate.setHours(23, 59, 59, 999);
        exportRequest.end_date = endDate.toISOString();
      }
    }

    await exportEvents(exportRequest);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33FF00] mx-auto mb-4"></div>
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              Loading analytics data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <BarChart3 className="w-12 h-12 mx-auto" />
            </div>
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              {error}
            </p>
            <Button
              onClick={refetch}
              className="mt-4"
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              No analytics data available
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { overview, metrics } = data;

  const toggleSeries = (seriesId: string) => {
    const newExpanded = new Set(expandedSeries);
    if (newExpanded.has(seriesId)) {
      newExpanded.delete(seriesId);
    } else {
      newExpanded.add(seriesId);
    }
    setExpandedSeries(newExpanded);
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      sort_order: 'oldest'
    });
    setDateError('');
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Enhanced Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 shadow-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent'}`}>
                  Analytics Dashboard
                </h1>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-0.5`}>
                  Track your event performance and guest engagement
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className={`flex items-center gap-2 transition-all duration-200 ${
                theme === 'dark'
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
            </Button>
            
            <Button
              onClick={clearFilters}
              variant="outline"
              className={`flex items-center gap-2 transition-all duration-200 ${
                theme === 'dark'
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Clear Filters</span>
            </Button>
          </div>
        </div>

        {/* Enhanced Filters Panel */}
        {showFilters && (
          <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 backdrop-blur-sm`}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <Filter className="w-5 h-5 text-blue-500" />
                Filter Options
              </h3>
              <Button
                onClick={() => setShowFilters(false)}
                variant="ghost"
                size="sm"
                className={`${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-full`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <DatePicker
                  label="Start Date"
                  value={filters.start_date}
                  maxDate={filters.end_date || undefined}
                  onChange={(value) => {
                    setFilters({ ...filters, start_date: value });
                  }}
                  placeholder="mm/dd/yyyy"
                  error={dateError && filters.start_date && filters.end_date ? dateError : undefined}
                />
              </div>
              
              <div>
                <DatePicker
                  label="End Date"
                  value={filters.end_date}
                  minDate={filters.start_date || undefined}
                  onChange={(value) => {
                    setFilters({ ...filters, end_date: value });
                  }}
                  placeholder="mm/dd/yyyy"
                  error={dateError && filters.start_date && filters.end_date ? dateError : undefined}
                />
              </div>

               <div>
                 <Select
                   label="Sort Order"
                   options={[
                     { value: 'oldest', label: 'Oldest to Newest' },
                     { value: 'newest', label: 'Newest to Oldest' }
                   ]}
                   value={filters.sort_order}
                   onChange={(value) => {
                     setFilters({ ...filters, sort_order: value as 'oldest' | 'newest' });
                   }}
                   placeholder="Select sort order"
                 />
               </div>
            </div>
          </div>
        )}

        {/* Overview Cards — aligned with dashboard style */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
          <AnalyticsCard
            title="Total Events"
            value={overview.total_events}
            icon={<Calendar className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Active Events"
            value={overview.active_events}
            icon={<BarChart3 className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Total Invitations"
            value={overview.total_invitations}
            icon={<Users className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Accepted Invitations"
            value={overview.accepted_invitations}
            subtitle={`${overview.rejected_invitations} rejected, ${overview.pending_invitations} pending`}
            icon={<UserCheck className="w-5 h-5" />}
          />
        </div>

        {/* Performance Metrics — aligned with dashboard style */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
          <AnalyticsCard
            title="Response Rate"
            value={`${metrics.response_rate}%`}
            icon={<TrendingUp className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Acceptance Rate"
            value={`${metrics.acceptance_rate}%`}
            icon={<UserCheck className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Companion Usage"
            value={`${metrics.companion_usage_rate}%`}
            icon={<Users className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Repeat Guests"
            value={metrics.repeat_guests}
            icon={<Clock className="w-5 h-5" />}
          />
        </div>

        {/* Enhanced Recent Events Table */}
        <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border overflow-hidden`}>
          <div className={`px-6 py-5 border-b ${theme === 'dark' ? 'border-gray-700 bg-gradient-to-r from-gray-700 to-gray-800' : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-blue-900' : 'bg-blue-100'}`}>
                <Activity className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Recent Events Performance
                </h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>
                  Your latest events and their performance metrics
                </p>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-4 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                    Event
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                    Date
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                    Invited
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                    Accepted
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                    Response Rate
                  </th>
                </tr>
              </thead>
              <tbody className={`${theme === 'dark' ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                {paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Calendar className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                        <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} font-medium`}>No events found</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Create your first event to see analytics</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                   paginatedItems.map((item) => {
                     if (item.type === 'series') {
                       const isExpanded = expandedSeries.has(item.seriesId!);
                       const seriesEvents = item.events;
                       
                       // Find the main event (is_main_event = true, or first by date)
                       const mainEvent = seriesEvents.find(e => e.is_main_event) || seriesEvents[0];
                       const subEvents = seriesEvents.filter(e => e.id !== mainEvent.id);
                       const hasSubEvents = subEvents.length > 0;
                       
                       const mainEventDate = new Date(mainEvent.event_date);
                       const isUpcoming = mainEventDate >= new Date();
                       
                       return (
                         <React.Fragment key={item.seriesId}>
                           {/* Series Header Row with Main Event Data */}
                           <tr 
                             className={`${theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors duration-150 ${hasSubEvents ? 'cursor-pointer' : ''}`}
                             onClick={hasSubEvents ? () => toggleSeries(item.seriesId!) : undefined}
                           >
                             <td className="px-6 py-4 whitespace-nowrap">
                               <div className="flex items-center gap-3">
                                 {hasSubEvents ? (
                                   isExpanded ? (
                                     <ChevronUp className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                   ) : (
                                     <ChevronDown className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                   )
                                 ) : (
                                   <div className="w-4 h-4" /> // Spacer to maintain alignment
                                 )}
                                 <div className={`w-2 h-2 rounded-full ${isUpcoming ? 'bg-green-500' : 'bg-gray-400'}`} />
                                 {/* Main event always has larger text (19px) */}
                                 <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} style={{ fontSize: '19px' }}>
                                   {mainEvent.title}
                                 </span>
                                 {hasSubEvents && (
                                   <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                     ({subEvents.length} {subEvents.length === 1 ? 'sub-event' : 'sub-events'})
                                   </span>
                                 )}
                               </div>
                             </td>
                             <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                               {mainEventDate.toLocaleDateString('en-US', { 
                                 year: 'numeric', 
                                 month: 'short', 
                                 day: 'numeric' 
                               })}
                             </td>
                             <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                               {mainEvent.total_invited}
                             </td>
                             <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                               {mainEvent.accepted_count}
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap">
                               <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${
                                 mainEvent.response_rate > 70 
                                   ? theme === 'dark'
                                     ? 'bg-green-900/30 text-green-400 border border-green-800'
                                     : 'bg-green-100 text-green-700 border border-green-200'
                                   : mainEvent.response_rate > 50
                                   ? theme === 'dark'
                                     ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'
                                     : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                   : theme === 'dark'
                                     ? 'bg-red-900/30 text-red-400 border border-red-800'
                                     : 'bg-red-100 text-red-700 border border-red-200'
                               }`}>
                                 {mainEvent.response_rate}%
                               </span>
                             </td>
                           </tr>
                           {/* Nested Sub-Events (excluding main event) */}
                           {isExpanded && subEvents.map((event, index) => {
                             const eventDate = new Date(event.event_date);
                             const isEventUpcoming = eventDate >= new Date();
                             const subEventNumber = index + 1;
                             const totalSubEvents = subEvents.length;
                             
                             return (
                               <tr 
                                 key={event.id} 
                                 className={`${theme === 'dark' ? 'bg-gray-750 hover:bg-gray-700/30' : 'bg-gray-50/50 hover:bg-gray-50'} transition-colors duration-150`}
                               >
                                 <td className="px-6 py-4 whitespace-nowrap">
                                   <div className="flex items-center gap-3 pl-12">
                                     <div className={`w-2 h-2 rounded-full ${
                                       isEventUpcoming ? 'bg-green-500' : 'bg-gray-400'
                                     }`} />
                                     <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                       {event.title}
                                     </span>
                                     <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                       ({subEventNumber} of {totalSubEvents})
                                     </span>
                                   </div>
                                 </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {eventDate.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {event.total_invited}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {event.accepted_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${
                            event.response_rate > 70 
                              ? theme === 'dark'
                                ? 'bg-green-900/30 text-green-400 border border-green-800'
                                : 'bg-green-100 text-green-700 border border-green-200'
                              : event.response_rate > 50
                              ? theme === 'dark'
                                ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'
                                : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                              : theme === 'dark'
                                ? 'bg-red-900/30 text-red-400 border border-red-800'
                                : 'bg-red-100 text-red-700 border border-red-200'
                          }`}>
                            {event.response_rate}%
                          </span>
                                 </td>
                               </tr>
                             );
                           })}
                         </React.Fragment>
                       );
                    } else {
                      // Standalone event - these are main events without sub-events
                      // All standalone events are main events, so they should have larger text (19px)
                      const event = item.events[0];
                      const eventDate = new Date(event.event_date);
                      const isUpcoming = eventDate >= new Date();
                      
                      return (
                        <tr 
                          key={event.id} 
                          className={`${theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors duration-150`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4" /> {/* Spacer to maintain alignment with series rows */}
                              <div className={`w-2 h-2 rounded-full ${
                                isUpcoming ? 'bg-green-500' : 'bg-gray-400'
                              }`} />
                              {/* Standalone events are main events without sub-events, so they have larger text (19px) */}
                              <span 
                                className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} 
                                style={{ fontSize: '19px' }}
                              >
                                {event.title}
                              </span>
                            </div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            {eventDate.toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            {event.total_invited}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            {event.accepted_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${
                              event.response_rate > 70 
                                  ? theme === 'dark'
                                  ? 'bg-green-900/30 text-green-400 border border-green-800'
                                  : 'bg-green-100 text-green-700 border border-green-200'
                                : event.response_rate > 50
                                  ? theme === 'dark'
                                    ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'
                                    : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                  : theme === 'dark'
                                  ? 'bg-red-900/30 text-red-400 border border-red-800'
                                  : 'bg-red-100 text-red-700 border border-red-200'
                            }`}>
                              {event.response_rate}%
                              </span>
                        </td>
                      </tr>
                    );
                    }
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`px-6 py-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-center sm:justify-between`}>
              <div className={`text-sm hidden sm:block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} items
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  className={`${theme === 'dark' ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Previous</span>
                </Button>
                {/* Mobile: Single page indicator */}
                <div className="sm:hidden flex items-center">
                  <span className={`px-3 py-1 text-sm font-medium rounded-md ${
                    theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-600 text-white'
                  }`}>
                    {currentPage} / {totalPages}
                  </span>
                </div>
                {/* Desktop: Multiple page numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          currentPage === pageNum
                            ? theme === 'dark'
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-600 text-white'
                            : theme === 'dark'
                              ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                  className={`${theme === 'dark' ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Export Section */}
        <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-purple-900' : 'bg-purple-100'}`}>
              <Download className={`w-5 h-5 ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Export Events Data
              </h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Download your analytics data in various formats
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleExport('csv')}
              disabled={exporting}
              variant="outline"
              className={`flex items-center gap-2 transition-all duration-200 disabled:opacity-50 ${
                theme === 'dark'
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Download className="w-4 h-4" />
              Export as CSV
            </Button>
            
            <Button
              onClick={() => handleExport('excel')}
              disabled={exporting}
              variant="outline"
              className={`flex items-center gap-2 transition-all duration-200 disabled:opacity-50 ${
                theme === 'dark'
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Download className="w-4 h-4" />
              Export as Excel
            </Button>
          </div>
          
          {exporting && (
            <div className={`mt-4 flex items-center gap-2 text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
              <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${theme === 'dark' ? 'border-blue-400' : 'border-blue-600'}`}></div>
              <span>Preparing your data for download...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostAnalyticsPage;

